// src/Login.js
import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CssBaseline,
  Box,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import backgroundImage from "../assets/document-processor-react.png";
import { styled } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../Constants/Constants";
import { toast, ToastContainer } from "react-toastify";
import CustomLoader from "../utils/CustomLoader";
import { useTranslation } from "react-i18next";
import { checkEmail } from "../utils/utils";
import LanguageSwitcher from "../components/LanguageSwitcher";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
  },
  imageSection: {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    color: "#fff",
    padding: theme.spacing(2),
  },
  formSection: {
    padding: theme.spacing(10),
  },
  formControl: {
    margin: theme.spacing(1, 0),
    width: "100%",
    maxWidth: "400px",
  },
  button: {
    marginTop: theme.spacing(3),
    width: "100%",
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  radioButton: {
    backgroundColor: theme.palette.secondary.main,
    width: "100px",
    height: "40px",
    borderRadius: "4px",
  },
  secondaryTexts: {
    color: theme.palette.secondary.black,
  },
  linkText: {
    textDecoration: "none",
    color: theme.palette.primary.main,
  },
  linkSecondaryText: {
    color: theme.palette.secondary.black,
  },
  bottomContainer: {
    textAlign: "center",
    marginTop: 50,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  languageSwitcherContainer: {
    position: "absolute",
    top: "20px",
    right: "30px",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
  },
}));

const Register = () => {
  const classes = useStyles();
  const [role, setRole] = useState("Admin");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isValidPassword, setIsValidPassword] = useState(true);
  const [isValidConfirmPassword, setIsValidConfirmPassword] = useState(true);
  const [isValidName, setIsValidName] = useState(true);

  const handleChangeName = (e) => {
    const value = e.target.value;
    setName(value);
    if (value === "") {
      setIsValidName(false);
    } else {
      setIsValidName(true);
    }
  };

  const handleChangeEmail = (e) => {
    const value = e.target.value;
    setUsername(value);
    if (value !== "") {
      setIsValidEmail(checkEmail(value));
    } else {
      setIsValidEmail(true);
    }
  };

  const handleChangePassword = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (value === "") {
      setIsValidPassword(false);
    } else {
      setIsValidPassword(true);
    }
  };

  const handleChangeConfirmPassword = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value !== password) {
      setIsValidConfirmPassword(false);
    } else {
      setIsValidConfirmPassword(true);
    }
  };

  const handleRegister = async () => {
    if (!isValidEmail) {
      toast.error("Invalid email address");
      setIsLoading(false);
      return;
    }
    if (password === "") {
      setIsValidPassword(false);
      setIsLoading(false);
      return;
    }
    if (confirmPassword !== password) {
      toast.error("Password not match");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const formData = { email: username, password: password, user_role: role };
      const response = await axios.post(`${BASE_URL}/user/register/`, formData);
      if (response) {
        toast.success("Registration Successful");
        navigate("/login");
      }
    } catch (err) {
      toast.error("Something wrong, try again");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <Grid container className={classes.root} spacing={0}>
        <Grid item xs={12} md={6} className={classes.imageSection}></Grid>
        <Grid item xs={12} md={6} className={classes.formSection}>
          <Box className={classes.languageSwitcherContainer}>
            <LanguageSwitcher 
              variant="login" 
              customStyle={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} 
            />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                align="left"
                variant="h1"
                fontWeight={700}
                gutterBottom
              >
                {t('common.register')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="name"
                label={t('common.name')}
                name="name"
                autoComplete="name"
                autoFocus
                value={name}
                onChange={handleChangeName}
                placeholder={t('common.enterName')}
                className={classes.textField}
                error={!isValidName}
                helperText={!isValidName && "Name is required"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="username"
                label={t('common.email')}
                name="username"
                autoComplete="email"
                value={username}
                onChange={handleChangeEmail}
                placeholder={t('common.enterEmail')}
                className={classes.textField}
                error={!isValidEmail}
                helperText={!isValidEmail && t('common.invalidEmail')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('common.password')}
                type="password"
                id="password"
                placeholder={t('common.enterPassword')}
                autoComplete="new-password"
                value={password}
                onChange={handleChangePassword}
                className={classes.textField}
                error={!isValidPassword}
                helperText={!isValidPassword && "Password is required"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('common.confirmPassword')}
                type="password"
                id="confirmPassword"
                placeholder={t('common.enterConfirmPassword')}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={handleChangeConfirmPassword}
                className={classes.textField}
                error={!isValidConfirmPassword}
                helperText={
                  !isValidConfirmPassword && t('common.passwordsDoNotMatch')
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={handleRegister}
                disabled={isLoading}
              >
                {isLoading ? <CustomLoader /> : t('common.register')}
              </Button>
            </Grid>
            <Grid item xs={12} className={classes.bottomContainer}>
              <Typography
                variant="body2"
                className={classes.linkSecondaryText}
                style={{ marginRight: "5px" }}
              >
                {t('common.alreadyHaveAccount')}
              </Typography>
              <Link to="/login" className={classes.linkText}>
                {t('common.signIn')}
              </Link>
            </Grid>
          </Grid>
        </Grid>
        <ToastContainer />
      </Grid>
    </>
  );
};

export default Register;
